import {
  BreachCode,
  BreachSeverity,
  ChangeOperation,
  CodingSystem,
  CoverageState,
  DecisionAuthority,
  DiagnosisAuthorType,
  DiagnosisRole,
  FulfillmentState,
  FulfillmentStopReason,
  GuaranteeDecision,
  GuaranteeProcessingState,
  GuaranteesRangePreset,
  MealRelation,
  PlanningSyncState,
  ScheduleTimeUnit,
  ServiceKind,
  V2GuaranteeCreatorType,
  VisitCategory,
} from '@lib/guarantees/data-access';

/**
 * The single vocabulary for every state in this UI — one tone, icon and key per enum value, so a
 * colour never means two things. Module-scoped: promote only once a second module needs it.
 */

export type ChipTone = 'neutral' | 'primary' | 'success' | 'warning' | 'error' | 'info';

/** The health manager's ruling — the strongest signal on a line, so it always reads as solid. */
export const DECISION_TONE: Record<GuaranteeDecision, ChipTone> = {
  Guaranteed: 'success',
  NotGuaranteed: 'error',
  Pending: 'warning',
};

export const DECISION_ICON: Record<GuaranteeDecision, string> = {
  Guaranteed: 'verified',
  NotGuaranteed: 'block',
  Pending: 'help',
};

/**
 * Stands in for the ruling's own icon where the engine decided rather than a person — the verdict is
 * unchanged, so the tone and the word stay; only the hand behind them is different.
 */
export const DECISION_AUTO_ICON = 'smart_toy';

export const DECISION_LABEL_KEY: Record<GuaranteeDecision, string> = {
  Guaranteed: 'guaranteeV2.label.decisionGuaranteed',
  NotGuaranteed: 'guaranteeV2.label.decisionNotGuaranteed',
  Pending: 'guaranteeV2.label.decisionPending',
};

export const DECISION_HINT_KEY: Record<GuaranteeDecision, string> = {
  Guaranteed: 'guaranteeV2.message.decisionGuaranteedHint',
  NotGuaranteed: 'guaranteeV2.message.decisionNotGuaranteedHint',
  Pending: 'guaranteeV2.message.decisionPendingHint',
};

/** Always label a reason: a line carries several at once, and unlabelled they read as one note. */
export const DECISION_REASON_LABEL_KEY: Record<GuaranteeDecision, string> = {
  Guaranteed: 'guaranteeV2.label.whyGuaranteed',
  NotGuaranteed: 'guaranteeV2.label.whyNotGuaranteed',
  Pending: 'guaranteeV2.label.decisionComment',
};

export const DECISION_AUTHORITY_HINT_KEY: Record<DecisionAuthority, string> = {
  Manual: 'guaranteeV2.message.authorityManualHint',
  Auto: 'guaranteeV2.message.authorityAutoHint',
};

/** The rule engine's opinion. Deliberately quieter than the ruling it only informs. */
export const COVERAGE_TONE: Record<CoverageState, ChipTone> = {
  Covered: 'success',
  NotCovered: 'error',
  NotEvaluated: 'neutral',
};

export const COVERAGE_ICON: Record<CoverageState, string> = {
  Covered: 'gpp_good',
  NotCovered: 'gpp_bad',
  NotEvaluated: 'gpp_maybe',
};

export const COVERAGE_LABEL_KEY: Record<CoverageState, string> = {
  Covered: 'guaranteeV2.label.coverageCovered',
  NotCovered: 'guaranteeV2.label.coverageNotCovered',
  NotEvaluated: 'guaranteeV2.label.coverageNotEvaluated',
};

export const COVERAGE_HINT_KEY: Record<CoverageState, string> = {
  Covered: 'guaranteeV2.message.coverageCoveredHint',
  NotCovered: 'guaranteeV2.message.coverageNotCoveredHint',
  NotEvaluated: 'guaranteeV2.message.coverageNotEvaluatedHint',
};

/** Delivery progress, which answers a different question from whether the insurer committed. */
export const FULFILLMENT_TONE: Record<FulfillmentState, ChipTone> = {
  NotPlanned: 'neutral',
  Planned: 'info',
  Rescheduled: 'info',
  InProgress: 'primary',
  Fulfilled: 'success',
  Stopped: 'error',
};

export const FULFILLMENT_ICON: Record<FulfillmentState, string> = {
  NotPlanned: 'radio_button_unchecked',
  Planned: 'event_upcoming',
  // Booked like `Planned` and drawn in the same tone: only the glyph says the slot was moved.
  Rescheduled: 'event_repeat',
  InProgress: 'pending',
  Fulfilled: 'check_circle',
  Stopped: 'cancel',
};

export const FULFILLMENT_LABEL_KEY: Record<FulfillmentState, string> = {
  NotPlanned: 'guaranteeV2.label.fulfillmentNotPlanned',
  Planned: 'guaranteeV2.label.fulfillmentPlanned',
  Rescheduled: 'guaranteeV2.label.fulfillmentRescheduled',
  InProgress: 'guaranteeV2.label.fulfillmentInProgress',
  Fulfilled: 'guaranteeV2.label.fulfillmentFulfilled',
  Stopped: 'guaranteeV2.label.fulfillmentStopped',
};

export const STOP_REASON_LABEL_KEY: Record<FulfillmentStopReason, string> = {
  Declined: 'guaranteeV2.label.stopReasonDeclined',
  Cancelled: 'guaranteeV2.label.stopReasonCancelled',
  NoShow: 'guaranteeV2.label.stopReasonNoShow',
  BookingFailed: 'guaranteeV2.label.stopReasonBookingFailed',
};

export const STOP_REASON_ICON: Record<FulfillmentStopReason, string> = {
  Declined: 'thumb_down',
  Cancelled: 'event_busy',
  NoShow: 'person_off',
  BookingFailed: 'sync_problem',
};

/**
 * How a delivery state reads on a chip.
 *
 * A stopped attempt is named by what stopped it: "Скасовано" says everything "Зупинено" says and
 * answers the question it leaves open, so the reason takes the state's place rather than sitting
 * beside it as a second chip. Every other state has only itself to report. The tone does not
 * change — a stop is a stop however it came about — so {@link FULFILLMENT_TONE} is read directly.
 */
export function fulfillmentIconFor(state: FulfillmentState, stopReason?: FulfillmentStopReason): string {
  return state === FulfillmentState.Stopped && stopReason ? STOP_REASON_ICON[stopReason] : (FULFILLMENT_ICON[state] ?? FULFILLMENT_ICON.NotPlanned);
}

export function fulfillmentLabelKeyFor(state: FulfillmentState, stopReason?: FulfillmentStopReason): string {
  return state === FulfillmentState.Stopped && stopReason ? STOP_REASON_LABEL_KEY[stopReason] : (FULFILLMENT_LABEL_KEY[state] ?? FULFILLMENT_LABEL_KEY.NotPlanned);
}

/** A booking mid-flight with the partner: what is on screen is not yet what is confirmed. */
export const SYNC_STATE_LABEL_KEY: Record<Exclude<PlanningSyncState, PlanningSyncState.None>, string> = {
  CreatePending: 'guaranteeV2.label.syncCreatePending',
  CancelPending: 'guaranteeV2.label.syncCancelPending',
  ReschedulePending: 'guaranteeV2.label.syncReschedulePending',
};

/** Which operation is in flight, so the flavour on a state says more than "something is syncing". */
export const SYNC_STATE_ICON: Record<Exclude<PlanningSyncState, PlanningSyncState.None>, string> = {
  CreatePending: 'more_time',
  CancelPending: 'cancel_schedule_send',
  ReschedulePending: 'edit_calendar',
};

/**
 * `None` is not a state to report — it is the absence of one, which is why the two records above
 * exclude it. Stated once: every screen showing a booking mid-flight has to make the same exclusion,
 * and one that forgot would announce "syncing" over a booking that has settled.
 */
export function syncStateLabelKey(state: PlanningSyncState): string {
  return state === PlanningSyncState.None ? '' : (SYNC_STATE_LABEL_KEY[state] ?? '');
}

export function syncStateIcon(state: PlanningSyncState): string {
  return state === PlanningSyncState.None ? '' : (SYNC_STATE_ICON[state] ?? 'sync');
}

export const SEVERITY_TONE: Record<BreachSeverity, ChipTone> = {
  Info: 'info',
  Warning: 'warning',
  Error: 'error',
};

export const BREACH_LABEL_KEY: Record<BreachCode, string> = {
  PrescriptionOrphanAppointment: 'guaranteeV2.label.breachPrescriptionOrphanAppointment',
  GuaranteeMissingVisitLink: 'guaranteeV2.label.breachGuaranteeMissingVisitLink',
  OrphanPrescription: 'guaranteeV2.label.breachOrphanPrescription',
  UnattributablePrescriptionPlanning: 'guaranteeV2.label.breachUnattributablePrescriptionPlanning',
  CompletedActionCountMismatch: 'guaranteeV2.label.breachCompletedActionCountMismatch',
  AppointmentStateNotReflected: 'guaranteeV2.label.breachAppointmentStateNotReflected',
  PendingPlanningStaleOrOrphan: 'guaranteeV2.label.breachPendingPlanningStaleOrOrphan',
  PlanningDuplicateActions: 'guaranteeV2.label.breachPlanningDuplicateActions',
  BrokenPlanningActionLink: 'guaranteeV2.label.breachBrokenPlanningActionLink',
  DuplicatePlanningEntries: 'guaranteeV2.label.breachDuplicatePlanningEntries',
  GuaranteeAppointmentMatch: 'guaranteeV2.label.breachGuaranteeAppointmentMatch',
};

/** Who asserted a diagnosis. A doctor's is the default reading, so only the others are announced. */
export const DIAGNOSIS_AUTHOR_HINT_KEY: Record<DiagnosisAuthorType, string> = {
  Doctor: 'guaranteeV2.message.diagnosisAuthorDoctor',
  HealthManager: 'guaranteeV2.message.diagnosisAuthorHealthManager',
  System: 'guaranteeV2.message.diagnosisAuthorSystem',
};

/** Waiting is a state, not a problem — the undecided count is what raises the alarm. */
export const STATUS_TONE: Record<GuaranteeProcessingState, ChipTone> = {
  WaitProcessing: 'info',
  Processed: 'success',
};

export const STATUS_LABEL_KEY: Record<GuaranteeProcessingState, string> = {
  WaitProcessing: 'guaranteeV2.label.statusWaitProcessing',
  Processed: 'guaranteeV2.label.statusProcessed',
};

export const STATUS_HINT_KEY: Record<GuaranteeProcessingState, string> = {
  WaitProcessing: 'guaranteeV2.message.statusWaitProcessingHint',
  Processed: 'guaranteeV2.message.statusProcessedHint',
};

/** Who raised the record. Read the same way on a guarantee and on any line under it. */
export const CREATOR_ICON: Record<V2GuaranteeCreatorType, string> = {
  Unknown: 'help_outline',
  Doctor: 'stethoscope',
  HealthManager: 'badge',
  User: 'person',
};

export const CREATOR_HINT_KEY: Record<V2GuaranteeCreatorType, string> = {
  Unknown: 'guaranteeV2.message.creatorUnknownHint',
  Doctor: 'guaranteeV2.message.creatorDoctorHint',
  HealthManager: 'guaranteeV2.message.creatorHealthManagerHint',
  User: 'guaranteeV2.message.creatorUserHint',
};

export const RANGE_PRESET_LABEL_KEY: Record<GuaranteesRangePreset, string> = {
  '3m': 'guaranteeV2.label.range3m',
  '6m': 'guaranteeV2.label.range6m',
  '12m': 'guaranteeV2.label.range12m',
  all: 'guaranteeV2.label.rangeAll',
};

export const SERVICE_KIND_ICON: Record<ServiceKind, string> = {
  Unknown: 'help_outline',
  Appointment: 'stethoscope',
  PersonalDoctorAppointment: 'clinical_notes',
  Medication: 'pill',
  InstrumentalDiagnostic: 'radiology',
  LaboratoryTest: 'labs',
  Other: 'medical_services',
};

export const SERVICE_KIND_LABEL_KEY: Record<ServiceKind, string> = {
  Unknown: 'guaranteeV2.label.kindUnknown',
  Appointment: 'guaranteeV2.label.kindAppointment',
  PersonalDoctorAppointment: 'guaranteeV2.label.kindPersonalDoctorAppointment',
  Medication: 'guaranteeV2.label.kindMedication',
  InstrumentalDiagnostic: 'guaranteeV2.label.kindInstrumentalDiagnostic',
  LaboratoryTest: 'guaranteeV2.label.kindLaboratoryTest',
  Other: 'guaranteeV2.label.kindOther',
};

export const VISIT_CATEGORY_ICON: Record<VisitCategory, string> = {
  Consultation: 'stethoscope',
  Diagnostics: 'radiology',
  Procedure: 'healing',
};

export const VISIT_CATEGORY_LABEL_KEY: Record<VisitCategory, string> = {
  Consultation: 'guaranteeV2.label.categoryConsultation',
  Diagnostics: 'guaranteeV2.label.categoryDiagnostics',
  Procedure: 'guaranteeV2.label.categoryProcedure',
};

/**
 * A visit need not state a category, and one it does state may be newer than this vocabulary —
 * either way it still reads as a visit rather than as a gap. Stated once, as the sync-state
 * readers are: every screen naming a visit has to make the same fallback.
 */
export function visitCategoryIcon(category: VisitCategory | undefined): string {
  return category ? (VISIT_CATEGORY_ICON[category] ?? 'event') : 'event';
}

export function visitCategoryLabelKey(category: VisitCategory | undefined): string {
  return category ? (VISIT_CATEGORY_LABEL_KEY[category] ?? 'guaranteeV2.label.visit') : 'guaranteeV2.label.visit';
}

export const MEAL_RELATION_LABEL_KEY: Record<MealRelation, string> = {
  Independent: 'guaranteeV2.label.mealIndependent',
  BeforeMeal: 'guaranteeV2.label.mealBefore',
  WithMeal: 'guaranteeV2.label.mealWith',
  AfterMeal: 'guaranteeV2.label.mealAfter',
};

export const TIME_UNIT_LABEL_KEY: Record<ScheduleTimeUnit, string> = {
  Hour: 'guaranteeV2.label.unitHour',
  Day: 'guaranteeV2.label.unitDay',
  Week: 'guaranteeV2.label.unitWeek',
  Month: 'guaranteeV2.label.unitMonth',
};

export const CODING_SYSTEM_LABEL_KEY: Record<CodingSystem, string> = {
  icd10: 'guaranteeV2.label.icd10',
  icpc2: 'guaranteeV2.label.icpc2',
};

export const DIAGNOSIS_ROLE_LABEL_KEY: Record<DiagnosisRole, string> = {
  Primary: 'guaranteeV2.label.rolePrimary',
  Comorbidity: 'guaranteeV2.label.roleComorbidity',
  Complication: 'guaranteeV2.label.roleComplication',
};

export const DECISION_AUTHORITY_LABEL_KEY: Record<DecisionAuthority, string> = {
  Manual: 'guaranteeV2.label.authorityManual',
  Auto: 'guaranteeV2.label.authorityAuto',
};

/** What a recorded version did. Named as the record's own timestamps are, so the two read alike. */
export const CHANGE_OPERATION_LABEL_KEY: Record<ChangeOperation, string> = {
  Create: 'guaranteeV2.label.createdAt',
  Update: 'guaranteeV2.label.updatedAt',
  Delete: 'guaranteeV2.label.deleted',
};

export const CHANGE_OPERATION_ICON: Record<ChangeOperation, string> = {
  Create: 'add_circle',
  Update: 'edit',
  Delete: 'delete',
};

export const CHANGE_OPERATION_TONE: Record<ChangeOperation, ChipTone> = {
  Create: 'success',
  Update: 'info',
  Delete: 'error',
};

/**
 * What each field of a stored snapshot is called, keyed by its path with collection positions
 * stripped. A path with no entry here is shown as itself rather than dropped: history that hides a
 * change because nobody has named its field yet is history that cannot be trusted.
 */
export const HISTORY_FIELD_LABEL_KEY: Record<string, string> = {
  // The guarantee itself
  status: 'guaranteeV2.label.status',
  description: 'guaranteeV2.label.guaranteeDescription',
  isServiceRequest: 'guaranteeV2.label.serviceRequest',
  isLiloStaff: 'guaranteeV2.label.liloStaff',
  isDeleted: 'guaranteeV2.label.deleted',
  deletionReason: 'guaranteeV2.label.deletionReason',
  creatorType: 'guaranteeV2.label.createdBy',
  'visit.appointmentId': 'guaranteeV2.label.appointment',
  'visit.appointmentDate': 'guaranteeV2.label.appointmentDate',
  'visit.partnerId': 'guaranteeV2.label.partner',
  'visit.doctorName': 'guaranteeV2.label.doctor',
  'visit.visitCategory': 'guaranteeV2.label.visitCategory',
  'carePlan.carePlanId': 'guaranteeV2.label.carePlan',
  'carePlan.diagnoses': 'guaranteeV2.label.guaranteeDiagnoses',
  'carePlan.diagnoses.role': 'guaranteeV2.label.role',
  'carePlan.diagnoses.icd10.code': 'guaranteeV2.label.icd10',
  'carePlan.diagnoses.icd10.name': 'guaranteeV2.label.diagnosisName',
  'carePlan.diagnoses.icpc2.code': 'guaranteeV2.label.icpc2',
  'carePlan.diagnoses.icpc2.name': 'guaranteeV2.label.diagnosisName',
  'carePlan.diagnoses.isSuspended': 'guaranteeV2.label.diagnosisSuspended',
  'carePlan.diagnoses.suspensionReason': 'guaranteeV2.label.suspensionReason',
  'carePlan.diagnoses.caseId': 'guaranteeV2.label.case',
  'carePlan.diagnoses.protocolId': 'guaranteeV2.label.protocol',
  'carePlan.diagnoses.authorType': 'guaranteeV2.label.diagnosisAuthor',
  'carePlan.diagnoses.authorName': 'guaranteeV2.label.diagnosisAuthor',
  'contract.contractId': 'guaranteeV2.label.contract',
  'contract.contractNumber': 'guaranteeV2.label.contractNumber',
  'contract.packageId': 'guaranteeV2.label.package',

  // The service line
  name: 'guaranteeV2.label.serviceName',
  code: 'guaranteeV2.label.serviceCode',
  kind: 'guaranteeV2.label.serviceKind',
  isFreeForm: 'guaranteeV2.label.freeForm',
  diagnosisId: 'guaranteeV2.label.diagnosis',
  completedInAppointment: 'guaranteeV2.label.completedInAppointment',
  completionReason: 'guaranteeV2.label.completionReason',
  editReason: 'guaranteeV2.label.editReason',
  medicalDataArrived: 'guaranteeV2.label.medicalData',
  categories: 'guaranteeV2.label.categories',
  'categories.categoryName': 'guaranteeV2.label.categories',
  conditions: 'guaranteeV2.label.conditions',
  'conditions.type': 'guaranteeV2.label.conditions',
  'conditions.value': 'guaranteeV2.label.conditions',
  'conditions.description': 'guaranteeV2.label.conditions',
  'grouping.groupKey': 'guaranteeV2.label.course',
  'grouping.groupOrdinal': 'guaranteeV2.label.courseOrdinal',
  'decision.decision': 'guaranteeV2.label.decision',
  'decision.reason.code': 'guaranteeV2.label.decisionReason',
  'decision.reason.name': 'guaranteeV2.label.decisionReason',
  'decision.note': 'guaranteeV2.label.decisionComment',
  'decision.authority': 'guaranteeV2.label.decisionAuthority',
  'coverage.state': 'guaranteeV2.label.coverage',
  'coverageEvaluation.state': 'guaranteeV2.label.coverage',
  'financials.reserved': 'guaranteeV2.label.reserved',
  'financials.coverageFranchise': 'guaranteeV2.label.coverageFranchise',
  'financials.considerPaid': 'guaranteeV2.label.considerPaid',

  // What the line consists of
  'details.code': 'guaranteeV2.label.serviceCode',
  'details.quantity': 'guaranteeV2.label.quantity',
  'details.startDate': 'guaranteeV2.label.periodStart',
  'details.endDate': 'guaranteeV2.label.periodEnd',
  'details.form': 'guaranteeV2.label.form',
  'details.comment': 'guaranteeV2.label.drugComment',
  'details.requiredQuantity': 'guaranteeV2.label.requiredQuantity',
  'details.treatmentPackages': 'guaranteeV2.label.treatmentPackages',
  'details.protocolIdentifier': 'guaranteeV2.label.protocolIdentifier',
  'details.drugs': 'guaranteeV2.label.trademarks',
  'details.schedule.dose': 'guaranteeV2.label.dose',
  'details.schedule.doseUnit': 'guaranteeV2.label.dose',
  'details.schedule.frequency': 'guaranteeV2.label.frequency',
  'details.schedule.period': 'guaranteeV2.label.schedule',
  'details.schedule.periodUnit': 'guaranteeV2.label.schedule',
  'details.schedule.duration': 'guaranteeV2.label.duration',
  'details.schedule.durationUnit': 'guaranteeV2.label.duration',
  'details.schedule.dailyDose': 'guaranteeV2.label.dailyDose',
  'details.schedule.courseDose': 'guaranteeV2.label.courseDose',
  'details.schedule.mealRelation': 'guaranteeV2.label.mealRelation',
  'details.schedule.timesOfDay': 'guaranteeV2.label.timesOfDay',
  'details.schedule.asNeeded': 'guaranteeV2.label.asNeeded',

  // How it was booked and delivered
  planning: 'guaranteeV2.label.planning',
  'planning.fulfillmentState': 'guaranteeV2.label.fulfillment',
  'planning.stopReason': 'guaranteeV2.label.stopReason',
  'planning.syncState': 'guaranteeV2.label.syncState',
  'planning.dateStart': 'guaranteeV2.label.plannedDate',
  'planning.dateEnd': 'guaranteeV2.label.periodEnd',
  'planning.partnerId': 'guaranteeV2.label.partner',
  'planning.partnerName': 'guaranteeV2.label.partner',
  'planning.locationId': 'guaranteeV2.label.location',
  'planning.locationAddress': 'guaranteeV2.label.location',
  'planning.price': 'guaranteeV2.label.price',
  'planning.effectiveFranchise': 'guaranteeV2.label.effectiveFranchise',
  'planning.doctorId': 'guaranteeV2.label.doctor',
  'planning.doctorName': 'guaranteeV2.label.doctor',
  'planning.appointmentId': 'guaranteeV2.label.appointment',
  'planning.orderId': 'guaranteeV2.label.orderId',
  'planning.orderNumber': 'guaranteeV2.label.orderNumber',
  'planning.prescriptionNumber': 'guaranteeV2.label.prescriptionNumber',
  'planning.meetingLink': 'guaranteeV2.label.meetingLink',
};

/** The fields whose value is a state rather than text, and the vocabulary that names each one. */
export const HISTORY_FIELD_VALUE_LABEL_KEY: Record<string, Record<string, string>> = {
  status: STATUS_LABEL_KEY,
  kind: SERVICE_KIND_LABEL_KEY,
  creatorType: CREATOR_HINT_KEY,
  'visit.visitCategory': VISIT_CATEGORY_LABEL_KEY,
  'carePlan.diagnoses.role': DIAGNOSIS_ROLE_LABEL_KEY,
  'carePlan.diagnoses.authorType': DIAGNOSIS_AUTHOR_HINT_KEY,
  'decision.decision': DECISION_LABEL_KEY,
  'decision.authority': DECISION_AUTHORITY_LABEL_KEY,
  'coverage.state': COVERAGE_LABEL_KEY,
  'coverageEvaluation.state': COVERAGE_LABEL_KEY,
  'planning.fulfillmentState': FULFILLMENT_LABEL_KEY,
  'planning.stopReason': STOP_REASON_LABEL_KEY,
  'planning.syncState': SYNC_STATE_LABEL_KEY,
  'details.schedule.mealRelation': MEAL_RELATION_LABEL_KEY,
  'details.schedule.periodUnit': TIME_UNIT_LABEL_KEY,
  'details.schedule.durationUnit': TIME_UNIT_LABEL_KEY,
};
